import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { workbenchStore } from '~/lib/stores/workbench';
import { useCodeDownload } from '~/lib/hooks/useCodeDownload';
import { buildLauncherSections, moveHighlight, type LauncherEntry } from './tool-launcher-model';
import { classNames } from '~/utils/classNames';
import { TOUCH_QUERY } from '~/lib/hooks/useMediaQuery';

export function ToolLauncher() {
  const tabs = useStore(workbenchStore.panelTabs);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const { existing, suggested, actions } = useMemo(
    () => buildLauncherSections(tabs, workbenchStore.getAvailableTools(), query),
    [tabs, query],
  );
  const flat = useMemo(() => [...existing, ...suggested, ...actions], [existing, suggested, actions]);
  const codeDownload = useCodeDownload();

  // The launcher is mounted when its tab activates — grab focus so typing filters immediately.
  // Not on touch devices: focusing raises the on-screen keyboard over the tool list.
  useEffect(() => {
    if (!window.matchMedia(TOUCH_QUERY).matches) {
      inputRef.current?.focus();
    }
  }, []);

  const activate = (entry: LauncherEntry) => {
    if (entry.kind === 'existing' && entry.tabId) {
      workbenchStore.setActiveTab(entry.tabId);
    } else if (entry.kind === 'suggested' && entry.tool) {
      workbenchStore.openTool(entry.tool);
    } else if (entry.action === 'download-code') {
      void codeDownload.download();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        if (h === -1) {
          return e.key === 'ArrowDown' ? 0 : flat.length - 1;
        }

        return moveHighlight(flat.length, h, e.key === 'ArrowDown' ? 1 : -1);
      });
    } else if (e.key === 'Enter' && highlight >= 0 && flat[highlight]) {
      e.preventDefault();
      activate(flat[highlight]);
    } else if (e.key === 'Escape') {
      setQuery('');
      setHighlight(-1);
    }
  };

  const renderRow = (entry: LauncherEntry) => {
    const flatIdx = flat.indexOf(entry);
    const isHighlighted = flatIdx === highlight;
    const busy = entry.action === 'download-code' && codeDownload.busy;

    return (
      <button
        key={entry.key}
        onClick={() => activate(entry)}
        onMouseMove={() => setHighlight(flatIdx)}
        className={classNames(
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer',
          'transition-colors duration-150',
          isHighlighted ? 'bg-raised' : 'hover:bg-raised',
        )}
      >
        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <div
            className={classNames(
              busy ? 'i-svg-spinners:ring-resize' : entry.icon,
              'text-lg',
              busy ? 'text-accent' : 'text-accent/70',
            )}
          />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-primary">{entry.label}</div>
          <div className="text-xs text-secondary truncate">{busy ? 'Packaging your code…' : entry.description}</div>
        </div>
      </button>
    );
  };

  return (
    <div className="h-full overflow-y-auto bg-page">
      <div className="max-w-xl mx-auto px-4 pt-6 sm:px-6 sm:pt-16 pb-8">
        <div className="relative mb-6">
          <div className="i-ph:magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(-1);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search for tools…"
            aria-label="Search for tools"
            className={classNames(
              'w-full pl-9 pr-3 py-2.5 rounded-lg text-sm text-primary placeholder:text-muted',
              'bg-raised border border-hairline outline-none transition-colors duration-150',
              'focus:border-accent/40 focus:ring-1 focus:ring-accent/30',
            )}
          />
        </div>

        {existing.length > 0 && (
          <div className="mb-5">
            <div className="text-xs text-muted mb-1.5 px-3">Jump to existing tab</div>
            <div className="flex flex-col gap-0.5">{existing.map(renderRow)}</div>
          </div>
        )}

        {suggested.length > 0 && (
          <div className="mb-5">
            <div className="text-xs text-muted mb-1.5 px-3">Suggested</div>
            <div className="flex flex-col gap-0.5">{suggested.map(renderRow)}</div>
          </div>
        )}

        {actions.length > 0 && (
          <div>
            <div className="text-xs text-muted mb-1.5 px-3">Actions</div>
            <div className="flex flex-col gap-0.5">{actions.map(renderRow)}</div>
          </div>
        )}

        {flat.length === 0 && (
          <div className="text-center py-10">
            <div className="text-sm text-primary mb-1">No tools match &lsquo;{query}&rsquo;</div>
            <div className="text-xs text-secondary">
              Try a different name, like &lsquo;analytics&rsquo; or &lsquo;forms&rsquo;.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
